import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog, { DialogButton } from '@/components/ui/Dialog';
import { useDriveSubFolders, useDriveFiles } from '@/hooks/useDocFlow';
import type { IDriveFile } from '@/types/docflow';

interface DriveBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'folder' | 'files';
  rootFolderId: string;
  onSelectFolder?: (folderId: string, folderName: string) => void;
  onSelectFiles?: (files: IDriveFile[]) => void;
}

const DriveBrowser: React.FC<DriveBrowserProps> = ({
  isOpen,
  onClose,
  mode,
  rootFolderId,
  onSelectFolder,
  onSelectFiles,
}) => {
  const { t } = useTranslation('docflow');
  const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string; name: string }[]>([
    { id: rootFolderId, name: 'Root' },
  ]);
  const [selectedFiles, setSelectedFiles] = useState<IDriveFile[]>([]);

  const { data: folders, isLoading: foldersLoading } = useDriveSubFolders(currentFolderId);
  const { data: files, isLoading: filesLoading } = useDriveFiles(currentFolderId);

  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setBreadcrumbs((prev) => [...prev, { id: folderId, name: folderName }]);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    const crumb = breadcrumbs[index];
    setCurrentFolderId(crumb.id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
  }, [breadcrumbs]);

  const toggleFileSelection = useCallback((file: IDriveFile) => {
    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.id === file.id);
      if (exists) {
        return prev.filter((f) => f.id !== file.id);
      }
      return [...prev, file];
    });
  }, []);

  const handleSelectFolder = useCallback(() => {
    const current = breadcrumbs[breadcrumbs.length - 1];
    onSelectFolder?.(current.id, current.name);
    onClose();
  }, [breadcrumbs, onSelectFolder, onClose]);

  const handleSelectFiles = useCallback(() => {
    onSelectFiles?.(selectedFiles);
    onClose();
  }, [selectedFiles, onSelectFiles, onClose]);

  const isFileSelected = (fileId: string) => selectedFiles.some((f) => f.id === fileId);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('drive.title')}
      headerColor="blue"
      maxWidth="2xl"
      footer={
        <>
          <DialogButton variant="secondary" onClick={onClose}>
            {t('common:cancel', 'Cancel')}
          </DialogButton>
          {mode === 'folder' ? (
            <DialogButton variant="primary" onClick={handleSelectFolder}>
              {t('config.selectFolder')}
            </DialogButton>
          ) : (
            <DialogButton
              variant="primary"
              onClick={handleSelectFiles}
              disabled={selectedFiles.length === 0}
            >
              {t('drive.selectFiles')} ({selectedFiles.length})
            </DialogButton>
          )}
        </>
      }
    >
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 mb-3 text-sm overflow-x-auto">
        {breadcrumbs.map((crumb, index) => (
          <div key={crumb.id} className="flex items-center gap-1 whitespace-nowrap">
            {index > 0 && <span className="text-gray-400">/</span>}
            <button
              onClick={() => navigateToBreadcrumb(index)}
              className={`hover:text-blue-600 dark:hover:text-blue-400 ${
                index === breadcrumbs.length - 1
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {crumb.name}
            </button>
          </div>
        ))}
      </div>

      {/* Folders */}
      <div className="mb-4">
        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">
          {t('drive.folders')}
        </h4>
        {foldersLoading ? (
          <div className="text-sm text-gray-400 py-2">Loading...</div>
        ) : !folders || folders.length === 0 ? (
          <div className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
            {t('drive.noFolders')}
          </div>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {folders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder.id, folder.name)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span className="truncate dark:text-gray-200">{folder.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Files (shown in both modes, but selectable only in "files" mode) */}
      {mode === 'files' && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">
            {t('drive.files')}
          </h4>
          {filesLoading ? (
            <div className="text-sm text-gray-400 py-2">Loading...</div>
          ) : !files || files.length === 0 ? (
            <div className="text-sm text-gray-400 dark:text-gray-500 italic py-2">
              {t('drive.noFiles')}
            </div>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {files.map((file) => (
                <label
                  key={file.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded cursor-pointer ${
                    isFileSelected(file.id)
                      ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isFileSelected(file.id)}
                    onChange={() => toggleFileSelection(file)}
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate dark:text-gray-200">{file.name}</span>
                  {file.size !== undefined && (
                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
              {t('drive.selectedCount', { count: selectedFiles.length })}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default DriveBrowser;
